/**
 * announce.ts — the canonical screen-reader live-region singleton for the a11y-view
 * substrate (#15 LIFT).
 *
 * ONE polite `aria-live` status region serves BOTH a11y surfaces — the classic
 * pdf.js `/editor` and the shared PPTX / DOCX / PDF diff-view mount — replacing the
 * per-surface ad-hoc writers the DRY Epic flagged: the editor's old `announce`
 * (`editor-empty-state.ts`) wrote to a hand-placed `#editor-a11y-live`, and the shared
 * mount had none. Extracted here so a fix-once holds for both (A.6 / rule #11); every
 * call site imports `announce` from HERE — no delegating shim (A.7).
 *
 * A screen reader voices whatever text lands in the region without moving focus, so
 * `announce` is the non-visual status channel (load progress, save results, honest
 * "not supported" messages) the surfaces share.
 *
 * @module a11y-view/announce
 */
// The visually-hidden recipe applied inline so the region stays invisible even on a
// host page that does not define a `.sr-only` rule — this module is format-agnostic
// and mounts onto arbitrary hosts, so it must not depend on page CSS.
const SR_ONLY_STYLE = {
    position: "absolute",
    width: "1px",
    height: "1px",
    margin: "-1px",
    padding: "0",
    border: "0",
    overflow: "hidden",
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    whiteSpace: "nowrap",
};
/** Stable id of the shared region, so a page may pre-place it and both surfaces adopt it. */
const REGION_ID = "a11y-live-region";
/**
 * Owns the single live region for the page. A class because the cached region element
 * is a long-lived handle that wants an owner (rule #11a); the module exports one
 * process-wide instance so callers write to the SAME region.
 */
class LiveRegionAnnouncer {
    _region = null;
    _regionEl() {
        if (this._region && this._region.isConnected)
            return this._region;
        const existing = document.getElementById(REGION_ID); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        if (existing) {
            this._region = existing;
            return existing;
        }
        const el = document.createElement("div"); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        el.id = REGION_ID;
        el.className = "sr-only";
        el.setAttribute("role", "status");
        el.setAttribute("aria-live", "polite");
        el.setAttribute("aria-atomic", "true");
        Object.assign(el.style, SR_ONLY_STYLE);
        document.body.appendChild(el); // noqa: no-global-read-in-frontend-production — dom-accessor-migration-pending
        this._region = el;
        return el;
    }
    /** Voice `text` to assistive tech via the shared region. Focus is never moved. */
    announce(text) {
        this._regionEl().textContent = text;
    }
}
/** The process-wide SR live-region singleton (one polite region per page). */
export const srAnnouncer = new LiveRegionAnnouncer();
/**
 * Announce `text` to assistive technology via the shared screen-reader live region.
 * The canonical status channel for both a11y surfaces; writing text a screen reader
 * voices without moving focus. Facade over {@link srAnnouncer}.
 */
export function announce(text) {
    srAnnouncer.announce(text);
}
